import React, { useEffect, useMemo, useState } from "react";
import RepairIssueDescription from "../RepairIssueDescription";
import { hydrateCollectInfo, hasNoiseFeedback } from "../EditingPage/hydrateCollectInfo";
import useNoiseInfo from "../store/useNoiseInfo";
import useRepairIssueInfo from "../store/useRepairIssueInfo";
import { NOISE_SUB_CODE } from "../config/constants";
import { RepairSubItem } from "../interfaces/home";
import styles from "./index.module.scss";

interface IssueDescriptionViewProps {
  /** 预约详情的采集内容 collectInfo（问题描述 / 异响 / 附件都在其中） */
  collectInfo: any;
  /** 返回（关闭浮层） */
  onClose: () => void;
}

/**
 * 「问题描述」只读查看浮层（参考「资料查看」InsuranceMaterial 的覆盖层方式，不走独立路由）。
 *
 * 由预约详情页「问题描述-查看」以覆盖层形式打开，复用问题描述页组件（RepairIssueDescription）的 readOnly 只读态，
 * 展示各故障维修子项的问题描述 / 附件 / 最近一次问题时间，以及异响子项的异响反馈；底部提供「返回」。
 *
 * 数据直接来自详情页已加载的 collectInfo（无需二次请求），
 * 通过 hydrateCollectInfo 水合到 useRepairIssueInfo / useNoiseInfo store，再由组件按 subItems 逐项读取。
 *
 * 注：faultFeedbackList 回传 itemCode + itemName，subItems 的 name 直接取 itemName。
 */
export default function IssueDescriptionView(props: IssueDescriptionViewProps) {
  const { collectInfo, onClose } = props;
  const { resetNoiseInfo, hydrateNoiseInfo } = useNoiseInfo();
  const { hydrateIssues } = useRepairIssueInfo();
  // 先水合再渲染内容，避免首帧 store 为空闪「—」/空附件
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    hydrateCollectInfo(collectInfo, {
      setAttachmentIds: () => {},
      resetNoiseInfo,
      hydrateNoiseInfo,
      hydrateIssues,
    });
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectInfo]);

  // 由 collectInfo 重建 subItems（非异响维修子项 + 异响子项）
  const subItems = useMemo<RepairSubItem[]>(() => {
    const collect = collectInfo || {};
    const faultList: any[] = Array.isArray(collect.faultFeedbackList)
      ? collect.faultFeedbackList
      : [];
    const items: RepairSubItem[] = faultList
      .filter((it) => it?.itemCode != null)
      .map((it) => ({
        code: Number(it.itemCode),
        // 后端 faultFeedbackList 回传 itemCode + itemName，直接取用
        name: it.itemName ?? "",
      }));

    // 异响子项（code=103）：与 hydrateCollectInfo 共用同一「是否存在异响反馈」判定
    if (hasNoiseFeedback(collect)) {
      items.push({ code: NOISE_SUB_CODE, name: "异响" });
    }
    return items;
  }, [collectInfo]);

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        {hydrated && <RepairIssueDescription subItems={subItems} readOnly />}
      </div>
      <div className={styles.bottomBar}>
        <div className={styles.returnBtn} onClick={onClose}>
          返回
        </div>
      </div>
    </div>
  );
}
